/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {Utils, AbstractGeometry, BoundingBox, EEmbededElementTypes, RenderDocument} from "js_renderer";
import Element from "./element";
import {mat4, vec3} from "gl-matrix";
import Factory from "./factory";
import WGS84Projection from "../terrain/wgs84_projection";
import IModel from "../structural_elements/model_interface";
import TerrainTile from "../terrain/terrain_tile";
import ITerrain from "../terrain/terrain_interface";

export default
class HeightMapTile extends Element {

    public static TypeName: string = EEmbededElementTypes.HEIGHTMAPTILE;
    private heightOffsets: Float32Array;
    private tessAreas: any[];
    private terrain: ITerrain;
    private dataTile: any;

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        this.terrain = null;
        this.dataTile = null;
        this.tessAreas = [];
        this.heightOffsets = null; // local tile height offset (the terrain offset should be used too when rendering)
    }

    public static create(aRenderDocument: RenderDocument): HeightMapTile {
        let element = new HeightMapTile(aRenderDocument);
        element._typeName = HeightMapTile.TypeName;
        return element;
    }

    public setTerrain(aTerrain: ITerrain) {
        this.terrain = aTerrain;
    }

    public getDataTile(): any {
        return this.dataTile;
    }

    public setDataTile(aDataTile) {
        this.dataTile = aDataTile;

        // create an empty aray for offset the elevations in order to e.g. make the close extent of a house flat
        this.heightOffsets = new Float32Array(this.dataTile.elevations.length);
    }

    private _getIndexToElevationsArray(aCol: number, aRow: number) {
        return (TerrainTile.TILE_SIZE.cols - aRow - 1) * TerrainTile.TILE_SIZE.cols + aCol;
    }

    public setHeight(aCol: number, aRow: number, aHeight: number) {
        this.dataTile.elevations[this._getIndexToElevationsArray(aCol, aRow)] = aHeight;
    }

    public setHeightOffset(aCol: number, aRow: number, aHeight: number): boolean {
        if (aCol < 0) {
            return false;
        }

        if (aRow < 0) {
            return false;
        }

        if (aCol >= TerrainTile.TILE_SIZE.cols) {
            return false;
        }

        if (aRow >= TerrainTile.TILE_SIZE.rows) {
            return false;
        }

        this.heightOffsets[this._getIndexToElevationsArray(aCol, aRow)] = aHeight;

        return true;
    }

    public clearHeightOffsets() {
        if (this.dataTile) {
            this.heightOffsets = new Float32Array(this.dataTile.elevations.length);
        }
    }

    public getHeightWithoutOffset(aCol: number, aRow: number): number {
        if (aCol < 0) {
            aCol = 0;
        }

        if (aRow < 0) {
            aRow = 0;
        }

        if (aCol >= TerrainTile.TILE_SIZE.cols) {
            aCol = TerrainTile.TILE_SIZE.cols - 1;
        }

        if (aRow >= TerrainTile.TILE_SIZE.rows) {
            aRow = TerrainTile.TILE_SIZE.rows - 1;
        }

        return this.getHeightWithOffsetNoCheckOfBoundaries(aCol, aRow);
    }

    public getHeightWithOffset(aCol: number, aRow: number): number {
        if (aCol < 0) {
            aCol = 0;
        }

        if (aRow < 0) {
            aRow = 0;
        }

        if (aCol >= TerrainTile.TILE_SIZE.cols) {
            aCol = TerrainTile.TILE_SIZE.cols - 1;
        }

        if (aRow >= TerrainTile.TILE_SIZE.rows) {
            aRow = TerrainTile.TILE_SIZE.rows - 1;
        }

        return this.getHeightWithOffsetNoCheckOfBoundaries(aCol, aRow);
    }

    public getHeightWithOffsetNoCheckOfBoundaries(aCol: number, aRow: number): number {
        let index = this._getIndexToElevationsArray(aCol, aRow);
        return this.dataTile.elevations[index] + this.heightOffsets[index];
    }

    public addTesselationArea(aTesselationArea: any) {
        this.tessAreas.push(aTesselationArea);
    }

    public tesselationAreasEmpty(): boolean {
        return this.tessAreas.length === 0;
    }

    protected _generateElementDescriptionJSON(): any {
        return {
            "type": this.getTypeName(),
            "id": this.getTypeName() + "_" + this._UID,
            "UID": this._UID,
            "volumes": [this._UID]
        };
    }

    public filter() {
        // use box filter to filter the heightmap
        let boxFilter = 1 / 9;
        for (let row = 1; row < TerrainTile.TILE_SIZE.rows - 2; row++) {
            for (let col = 1; col < TerrainTile.TILE_SIZE.cols - 2; col++) {
                let height = 0;
                for (let i = -1; i < 2; i++) {
                    for (let j = -1; j < 2; j++) {
                        height += this.getHeightWithoutOffset(col + i, row + j) * boxFilter;
                    }
                }
                this.setHeight(col, row, height);
            }
        }
    }

    public subSample(aTileSize: { cols: number, rows: number }) {
        let subsampledCols = Math.floor((TerrainTile.TILE_SIZE.cols + 1) / aTileSize.cols);
        let subsampledRows = Math.floor((TerrainTile.TILE_SIZE.rows + 1) / aTileSize.rows);

        for (let srow = 0; srow < subsampledRows; srow++) {
            for (let scol = 0; scol < subsampledCols; scol++) {
                for (let row = 0; row < aTileSize.rows; row++) {
                    for (let col = 0; col < aTileSize.cols; col++) {

                        let cx = col * (1.0 / (aTileSize.cols - 1));
                        let cy = row * (1.0 / (aTileSize.rows - 1));

                        let subsampledCol = scol * aTileSize.cols;
                        let subsampledRow = srow * aTileSize.rows;

                        let A = this.getHeightWithoutOffset(subsampledCol, subsampledRow);
                        let B = this.getHeightWithoutOffset(subsampledCol + aTileSize.cols - 1, subsampledRow);
                        let C = this.getHeightWithoutOffset(subsampledCol, subsampledRow + aTileSize.rows - 1);
                        let D = this.getHeightWithoutOffset(subsampledCol + aTileSize.cols - 1, subsampledRow + aTileSize.rows - 1);

                        let height = A * (1 - cx) * (1 - cy) + B * cx * (1 - cy) + C * (1 - cx) * cy + D * cx * cy;
                        this.setHeight(subsampledCol + col, subsampledRow + row, height);
                    }
                }
            }
        }
    }

    private _computeNormals(aNormals: Float32Array) {
        // compute normals
        let normal = vec3.create();
        let nx = 0;
        let nz = 0;
        let index = 0;
        for (let row = 0; row < TerrainTile.TILE_SIZE.rows; row++) {
            for (let col = 0; col < TerrainTile.TILE_SIZE.cols; col++) {
                index = ((row * TerrainTile.TILE_SIZE.cols) + col) * 3; // index to normal array

                nx = -(this.getHeightWithOffset(col + 1, row) - this.getHeightWithOffset(col - 1, row));
                nz = this.getHeightWithOffset(col, row + 1) - this.getHeightWithOffset(col, row - 1);

                if (col === 0 || col === (TerrainTile.TILE_SIZE.cols - 1)) {
                    nx *= 2;
                }

                if (row === 0 || row === (TerrainTile.TILE_SIZE.rows - 1)) {
                    nz *= 2;
                }

                normal[0] = nx;
                normal[1] = 2;
                normal[2] = nz;

                vec3.normalize(normal, normal);
                aNormals[index + 0] = normal[0];
                aNormals[index + 1] = normal[1];
                aNormals[index + 2] = normal[2];
            }
        }
    }

    private _computeTextureCoordinates(aTextureCoords: Float32Array) {
        let pixelsPerStep = [
            this.dataTile.textureSize.width / (TerrainTile.TILE_SIZE.cols - 1),
            this.dataTile.textureSize.height / (TerrainTile.TILE_SIZE.rows - 1)
        ];

        let texCoordStep = [
            pixelsPerStep[0] / this.dataTile.textureSize.width,
            pixelsPerStep[1] / this.dataTile.textureSize.height
        ];

        let index = 0;
        for (let row = 0; row < TerrainTile.TILE_SIZE.rows; row++) {
            for (let col = 0; col < TerrainTile.TILE_SIZE.cols; col++) {
                index = ((row * TerrainTile.TILE_SIZE.cols) + col) * 2;
                aTextureCoords[index + 0] = texCoordStep[0] * col;
                aTextureCoords[index + 1] = texCoordStep[1] * row;
            }
        }
    }

    private _computeIndices(aIndices: number[]) {
        for (let i = 0; i < this.tessAreas.length; i++) {
            let tessArea = this.tessAreas[i];
            let revert;
            for (let row = tessArea.offset.y; row < (tessArea.offset.y + tessArea.size.rows - 1); row++) {
                for (let col = tessArea.offset.x; col < (tessArea.offset.x + tessArea.size.cols - 1); col++) {
                    revert = (col % 2 === row % 2);
                    if (!revert) {
                        aIndices.push(((row + 0) * TerrainTile.TILE_SIZE.cols) + (col + 0));
                        aIndices.push(((row + 1) * TerrainTile.TILE_SIZE.cols) + (col + 0));
                        aIndices.push(((row + 0) * TerrainTile.TILE_SIZE.cols) + (col + 1));

                        aIndices.push(((row + 0) * TerrainTile.TILE_SIZE.cols) + (col + 1));
                        aIndices.push(((row + 1) * TerrainTile.TILE_SIZE.cols) + (col + 0));
                        aIndices.push(((row + 1) * TerrainTile.TILE_SIZE.cols) + (col + 1));
                    } else {
                        aIndices.push(((row + 0) * TerrainTile.TILE_SIZE.cols) + (col + 0));
                        aIndices.push(((row + 1) * TerrainTile.TILE_SIZE.cols) + (col + 0));
                        aIndices.push(((row + 1) * TerrainTile.TILE_SIZE.cols) + (col + 1));

                        aIndices.push(((row + 0) * TerrainTile.TILE_SIZE.cols) + (col + 0));
                        aIndices.push(((row + 1) * TerrainTile.TILE_SIZE.cols) + (col + 1));
                        aIndices.push(((row + 0) * TerrainTile.TILE_SIZE.cols) + (col + 1));
                    }
                }
            }
        }
    }

    private _triangulateTile(aVertices: Float32Array, aNormals: Float32Array, aTexCoordinates: Float32Array, aIndices: number[]) {
        let proj = new WGS84Projection();
        let terrainCenter = this.terrain.getCenter();

        let center = proj.lngLatToTileXY(terrainCenter.lng, terrainCenter.lat, this.dataTile.level.topZoomLevel, false);

        let factor = Math.pow(2.0, this.dataTile.level.topZoomLevel - this.dataTile.level.zoomLevel);

        // the formula is here https://msdn.microsoft.com/en-us/library/bb259689.aspx
        let groundResolutionPerPixelX = Math.cos(terrainCenter.lat * Math.PI / 180.0) * 2 * Math.PI * 6378137.0 / (this.dataTile.textureSize.width * Math.pow(2, this.dataTile.level.zoomLevel));
        let groundResolutionPerPixelY = Math.cos(terrainCenter.lat * Math.PI / 180.0) * 2 * Math.PI * 6378137.0 / (this.dataTile.textureSize.height * Math.pow(2, this.dataTile.level.zoomLevel));

        let stepX = (groundResolutionPerPixelX * this.dataTile.textureSize.width) / (TerrainTile.TILE_SIZE.cols - 1);
        let stepY = (groundResolutionPerPixelY * this.dataTile.textureSize.height) / (TerrainTile.TILE_SIZE.rows - 1);

        let tileSizeX = stepX * (TerrainTile.TILE_SIZE.cols - 1);
        let tileSizeY = stepY * (TerrainTile.TILE_SIZE.rows - 1);

        let tileXOffset = this.terrain.getXOffset() + tileSizeX * (this.dataTile.x - center.x / factor);
        let tileYOffset = this.terrain.getYOffset() + tileSizeY * (this.dataTile.y - center.y / factor);

        // create vertices
        let index = 0;
        for (let row = 0; row < TerrainTile.TILE_SIZE.rows; row++) {
            for (let col = 0; col < TerrainTile.TILE_SIZE.cols; col++) {
                aVertices[index * 3 + 0] = tileXOffset + (stepX * col);
                aVertices[index * 3 + 1] = -this.terrain.getHeightOffset() + this.getHeightWithOffsetNoCheckOfBoundaries(col, row);
                aVertices[index * 3 + 2] = tileYOffset + (stepY * row);
                this._properties.bbox.extendByXYZ(aVertices[index * 3 + 0], aVertices[index * 3 + 1], aVertices[index * 3 + 2]);
                index++;
            }
        }

        // create normals
        this._computeNormals(aNormals);

        // create texture coordinates
        this._computeTextureCoordinates(aTexCoordinates);

        // create indices
        this._computeIndices(aIndices);
    }

    private _generateBinaryData(): ArrayBuffer {
        this.extendProperties({
            bbox: new BoundingBox,
            verticesCount: TerrainTile.TILE_SIZE.cols * TerrainTile.TILE_SIZE.rows,
            normalsCount: TerrainTile.TILE_SIZE.cols * TerrainTile.TILE_SIZE.rows,
            texCoordCount: TerrainTile.TILE_SIZE.cols * TerrainTile.TILE_SIZE.rows,
            indexCount: 0 // computed later
        });

        let vertices = new Float32Array(this._properties.verticesCount * 3);  // * 3 == xyz
        let normals = new Float32Array(this._properties.normalsCount * 3);   // * 3 == xyz
        let texCoordinates = new Float32Array(this._properties.texCoordCount * 2); // *2 == uv
        let indicesArray: number[] = [];//new Uint32Array(this._properties.indexCount);        // for indexed GL_TRIANGLES

        this._triangulateTile(vertices, normals, texCoordinates, indicesArray);

        // based on zeropoint rotation we rotate the tile according the angle in the zeropoint
        // because terrain
        let mainModel: IModel = this._sceneAPI.getDocument().getMainModel();
        let zeropoint = mainModel.getZeropointSettingsRef().zeropoint;
        if (zeropoint && zeropoint.getRotation() !== 0.0) {
            let yRotMatrix = mat4.create();
            mat4.rotateY(yRotMatrix, yRotMatrix, -zeropoint.getRotation());

            vertices =
                AbstractGeometry.transformArray(
                    vertices,
                    vertices.length / 3,
                    yRotMatrix
                );

            normals =
                AbstractGeometry.transformArray(
                    normals,
                    normals.length / 3,
                    yRotMatrix
                );
        }

        // get the indices count
        // @ts-ignore
        let indices = new Uint32Array(indicesArray, indicesArray.length);
        this._properties.indexCount = indices.byteLength / 4;

        this._properties.binarySize =
            4 * 3 * this._properties.verticesCount +
            4 * 3 * this._properties.normalsCount +
            4 * 2 * this._properties.texCoordCount +
            4 * 1 * this._properties.indexCount;

        let binarydata = Utils.mergeBuffers(vertices.buffer, normals.buffer);
        binarydata = Utils.mergeBuffers(binarydata.buffer, texCoordinates.buffer);
        binarydata = Utils.mergeBuffers(binarydata.buffer, indices.buffer);

        return binarydata.buffer;
    }

    /***
     * We generate the base64 encoded binary data here as we would like to use the binaryGeometry class which accepts as input a response which is an array with data
     * @returns {{description: {id: string, type: string, bboxMin: *, bboxMax: *, color: Array}, appearance: {renderOption: {alwaysOnTop: boolean, pickable: boolean}}, binaryGeometry: {vertexCount: number, indexCount: number, offset: number, binSize: number, src: string}}}
     * @private
     */
    protected _generateGeometryObjectDescriptionJSON(): any {
        let data = this._generateBinaryData();

        return [{
            description: {
                id: this._UID,
                type: "SHAPE",
                bboxMin: [this._properties.bbox.min[0], this._properties.bbox.min[1], this._properties.bbox.min[2]],
                bboxMax: [this._properties.bbox.max[0], this._properties.bbox.max[1], this._properties.bbox.max[2]],
                contributeToNearPlaneClipping: false
            },
            appearance: {
                renderOption: {
                    vertexOrdering: "CCW" // enable back face culling
                },
                imageTexture: {
                    url: [this.dataTile.url],
                    width: [this.dataTile.textureSize.width],
                    height: [this.dataTile.textureSize.height],
                    offsets: [0],
                    sizes: [0],
                    tiled: [false],
                    alpha: false,
                    model: "REPLACE",
                    blendColor: [0.0, 0.0, 0.0],
                    repeatS: 0,
                    repeatT: 0
                }
            },
            binaryGeometry: {
                vertexCount: this._properties.verticesCount,
                normalCount: this._properties.normalsCount,
                texcoordCount: this._properties.texCoordCount,
                indexCount: this._properties.indexCount,
                faceSize: [this._properties.indexCount / 3],
                offset: 0,
                binSize: this._properties.binarySize,
                src: this._renderDocument.getRenderer()._dataStorage.createLocalResourceFromArrayBuffer(data)
            }
        }];
    }
}

Factory.register(HeightMapTile);
